import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import { message } from 'antd';
import { DASHBOARD_QUERY_STUDENT_LIST, QUERY_COUNTRY } from '../queries';
import { removeSpace } from './helpers';
import generatePath from '~settings/routing';
import { useHistory } from 'react-router-dom';
import event from '~helpers/mixpanel';
import { pageType } from '~constants/pages';

export const useStudentList = () => {
  const history = useHistory();
  const landlordId = useSelector(state => state?.auth?.getIn(['base', 'landlordId']));
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [studentId, setStudentId] = useState(null);
  const [showListHeaderFilters, setShowListHeaderFilters] = useState({
    fullName: false,
    referenceId: false,
    externalId: false,
    registeredPhone: false,
    emailAddress: false,
    sourceCountryCodes: false,
    partnerCodes: false,
  });

  const [queryFilter, setQueryFilter] = useState({
    fullName: null,
    referenceId: null,
    externalId: null,
    registeredPhone: null,
    emailAddress: null,
    sourceCountryCodes: [],
    countrySearchKeyword: null,
    partnerCodeSearchKeyword: null,
  });

  const isValidFilter = Object.entries(queryFilter).some(([_key, value]) => {
    _key;
    return !(value === null || value === undefined || (Array.isArray(value) && value.length === 0));
  });

  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
  });

  const {
    loading,
    data: studentsListResponse,
    refetch: refetchStudentList,
  } = useQuery(DASHBOARD_QUERY_STUDENT_LIST, {
    variables: {
      pageNumber: pagination.pageNumber,
      pageSize: pagination.pageSize,
      sortBy: 'UPDATED_AT',
      sortDirection: 'DESCENDING',
      fullName: queryFilter.fullName,
      referenceId: queryFilter.referenceId,
      externalId: queryFilter.externalId,
      registeredPhone: queryFilter.registeredPhone,
      emailAddress: queryFilter.emailAddress,
      sourceCountryCodes: queryFilter.sourceCountryCodes.length
        ? queryFilter.sourceCountryCodes
        : null,
    },
    fetchPolicy: 'cache-and-network',
    onError: e => {
      message.error(e?.toString());
    },
  });

  const { loading: searchCountryLoading, data: searchCountryResponse } = useQuery(QUERY_COUNTRY, {
    variables: {
      pageNumber: 1,
      pageSize: 100,
      name: queryFilter.countrySearchKeyword,
    },
    onError: e => {
      message.error(e?.toString());
    },
  });

  const handleAddNoteClick = (e, record) => {
    e.preventDefault();
    e.stopPropagation();

    const studentId = record?.id;
    setIsModalOpen(true);
    setStudentId(studentId);
    return;
  };

  const handleAddedNote = () => {
    setIsModalOpen(false);
    setStudentId(null);
    refetchStudentList();
  };

  const handleAddNoteClose = () => {
    setIsModalOpen(false);
    setStudentId(null);
  };

  const handleGoToCheck = (e, id) => {
    e.stopPropagation();
    e.preventDefault();
    history.push(generatePath('students.detail', { id, ongoing: false }, {}, 'notes'));
  };

  const searchCountryResult = searchCountryResponse?.countries?.edges?.map(edge => ({
    label: edge.node.name,
    value: edge.node.countryCode,
  }));

  const studentsList = useMemo(() => {
    return studentsListResponse?.listStudents?.edges.map(edge => edge.node) || [];
  }, [studentsListResponse]);

  const listTotalCount = useMemo(() => {
    return studentsListResponse?.listStudents?.totalCount || 0;
  }, [studentsListResponse]);

  useEffect(() => {
    event('Viewed Student List Page', { 'Current Page Type': pageType.studentListPage });
  }, []);

  return {
    loading,
    studentsList,
    studentId,
    landlordId,
    listTotalCount,
    pagination,
    queryFilter,
    showListHeaderFilters,
    isValidFilter,
    isModalOpen,
    searchCountryLoading,
    searchCountryResult,
    removeSpace,
    setPagination,
    setQueryFilter,
    setIsModalOpen,
    setStudentId,
    setShowListHeaderFilters,
    handleAddNoteClick,
    handleAddNoteClose,
    handleAddedNote,
    handleGoToCheck,
  };
};
